// frontend/src/pages/settings/users-roles/UsersTab.tsx
import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Pencil, Power, RefreshCw, User, UserPlus } from 'lucide-react';
import { usersApi, rbacApi } from './api';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import { useAuthStore } from '../../../stores/authStore';
import { UserFormModal } from './UserFormModal';
import { PasswordOnceDialog } from './PasswordOnceDialog';
import type { RoleSummary, SellerOption, UserRow } from './types';

export function UsersTab() {
  const me = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [sellers, setSellers] = useState<SellerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{ open: boolean; user: UserRow | null } | null>(null);
  const [shown, setShown] = useState<{ username: string; password: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersApi.list();
      if (res.success && res.data) { setUsers(res.data); setError(null); }
      else setError(res.error || 'No se pudieron cargar los usuarios');
    } catch {
      // Se cortó la conexión: sin esto la pantalla se quedaba girando para siempre.
      setError('No se pudieron cargar los usuarios. Revisá la conexión y probá de nuevo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    (async () => {
      try {
        const r = await rbacApi.listRoles();
        if (r.success && r.data) setRoles(r.data);
        else setError(r.error || 'No se pudieron cargar los roles');
      } catch {
        setError('No se pudieron cargar los roles. Revisá la conexión y probá de nuevo.');
      }
    })();
    (async () => {
      try {
        const r = await usersApi.sellers();
        if (r.success && r.data) setSellers(r.data.sellers ?? []);
        else setError(r.error || 'No se pudieron cargar los vendedores');
      } catch {
        setError('No se pudieron cargar los vendedores. Revisá la conexión y probá de nuevo.');
      }
    })();
  }, [load]);

  const sellerName = (id: number) => sellers.find((s) => s.id === id)?.nickname ?? `Vendedor #${id}`;
  const sellersLabel = (u: UserRow) =>
    u.seller_ids === null ? 'Todos' : u.seller_ids.length === 0 ? 'Ninguno' : u.seller_ids.map(sellerName).join(', ');

  const toggleActive = async (u: UserRow) => {
    const verb = u.is_active ? 'Desactivar' : 'Activar';
    if (!window.confirm(`${verb} a ${u.display_name || u.username}?`)) return;
    try {
      const res = await usersApi.update(u.id, { is_active: !u.is_active });
      if (!res.success) { setError(res.error || `No se pudo ${verb.toLowerCase()} el usuario`); return; }
      setError(null);
      await load();
    } catch {
      setError(`No se pudo ${verb.toLowerCase()} el usuario. Revisá la conexión y probá de nuevo.`);
    }
  };

  const resetPassword = async (u: UserRow) => {
    if (!window.confirm(`Generar una contraseña nueva para ${u.display_name || u.username}? La actual dejará de servir.`)) return;
    try {
      const res = await usersApi.resetPassword(u.id);
      if (!res.success || !res.data) { setError(res.error || 'No se pudo resetear la contraseña'); return; }
      setError(null);
      setShown({ username: u.username, password: res.data.generated_password });
      await load();
    } catch {
      setError('No se pudo resetear la contraseña. Revisá la conexión y probá de nuevo.');
    }
  };

  const btn = 'px-2 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--color-text-muted)]">
          Alta, edición, contraseña y activación de las personas que entran al sistema.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]" title="Actualizar">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setForm({ open: true, user: null })}
            className="px-3 py-1.5 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 inline-flex items-center gap-1.5"
          >
            <UserPlus className="w-4 h-4" /> Nuevo usuario
          </button>
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg text-xs bg-red-500/10 border border-red-500/30 text-red-600">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-8 text-[var(--color-text-muted)]"><RefreshCw className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="divide-y divide-[var(--color-border)] border border-[var(--color-border)] rounded-lg overflow-hidden">
          {users.length === 0 && (
            <div className="px-4 py-6 text-sm text-center text-[var(--color-text-muted)]">No hay usuarios todavía.</div>
          )}
          {users.map((u) => {
            const isSelf = me?.id === u.id;
            // Quien no es Administrador no puede tocar a un Administrador (el servidor también lo corta).
            const blockedByAdmin = !me?.is_admin && !!u.is_admin;
            const editDisabled = blockedByAdmin;
            const toggleDisabled = isSelf || blockedByAdmin;
            const blockedTitle = blockedByAdmin ? 'Solo un Administrador puede modificar a otro Administrador' : undefined;

            return (
              <div key={u.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
                <div className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${u.is_active ? 'bg-[var(--color-primary-light)]' : 'bg-[var(--color-border)]'}`}>
                  <User className={`w-4 h-4 ${u.is_active ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-muted)]'}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[var(--color-text)]">{u.display_name || u.username}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-500">{u.role_name || u.role}</span>
                    <StatusBadge status={u.is_active ? 'ok' : 'error'} label={u.is_active ? 'Activo' : 'Inactivo'} size="sm" />
                    {u.must_change_password && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/30">
                        Debe cambiar contraseña
                      </span>
                    )}
                    {isSelf && <span className="text-[10px] text-[var(--color-text-muted)]">(vos)</span>}
                  </div>
                  <div className="text-xs text-[var(--color-text-muted)] truncate">
                    @{u.username} · Vendedores: {sellersLabel(u)}
                  </div>
                </div>

                <div className="text-xs text-[var(--color-text-muted)] sm:text-right sm:w-40">
                  <p>Último acceso</p>
                  <p className="font-medium">{u.last_login ? new Date(u.last_login).toLocaleString() : 'Nunca'}</p>
                </div>

                {/* Un botón deshabilitado no muestra su propio `title`: el motivo va en el
                    envoltorio, que sí recibe el paso del mouse. */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span title={blockedTitle}>
                    <button className={btn} disabled={editDisabled} onClick={() => setForm({ open: true, user: u })}>
                      <Pencil className="w-3 h-3" /> Editar
                    </button>
                  </span>
                  <span title={blockedTitle}>
                    <button className={btn} disabled={blockedByAdmin} onClick={() => resetPassword(u)}>
                      <KeyRound className="w-3 h-3" /> Resetear contraseña
                    </button>
                  </span>
                  <span title={isSelf ? 'No podés desactivarte a vos mismo' : blockedTitle}>
                    <button className={btn} disabled={toggleDisabled} onClick={() => toggleActive(u)}>
                      <Power className="w-3 h-3" /> {u.is_active ? 'Desactivar' : 'Activar'}
                    </button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {form?.open && (
        <UserFormModal
          user={form.user}
          roles={roles}
          sellers={sellers}
          actorIsAdmin={!!me?.is_admin}
          isSelf={me?.id === form.user?.id}
          onClose={() => setForm(null)}
          onSaved={async (result) => {
            setForm(null);
            setError(null);
            if (result.generated_password) {
              setShown({ username: result.user.username, password: result.generated_password });
            }
            await load();
          }}
        />
      )}

      {shown && <PasswordOnceDialog username={shown.username} password={shown.password} onClose={() => setShown(null)} />}
    </div>
  );
}
